import type { Session } from '@supabase/supabase-js';
import { supabase, withTimeout } from './supabase';

/**
 * Single source of truth for the current Supabase session on the client.
 *
 * Root-cause fix (2026-09-20): the app used to have THREE independent
 * `supabase.auth.onAuthStateChange` subscriptions (App.tsx, UserContext.tsx,
 * LoginPage.tsx) and dozens of ad-hoc `await supabase.auth.getSession()`/
 * `getUser()` calls, every one of them a real call into GoTrueClient's
 * internal auth lock. GoTrueClient serializes ALL of getSession()/getUser()/
 * refreshSession()/the auto-refresh tick/onAuthStateChange's own initial
 * emission behind that one lock -- if any single call hangs (slow/dead
 * network, a tab waking up from being suspended, Supabase Auth briefly
 * degraded), everything else queued behind it hangs too, including calls
 * that would otherwise be instant. That is the actual mechanism behind the
 * "stuck on Loading... forever" bug.
 *
 * This module is the ONLY place in the app that keeps a live session
 * reference and the ONLY place with a standing `onAuthStateChange`
 * subscription. It is a synchronized MIRROR of Supabase's own session, not
 * an independent store: the in-memory cache only ever changes in reaction
 * to (a) the one seed call below or (b) an `onAuthStateChange` event, both
 * of which are Supabase's own canonical signals. Everything else in the
 * app should read `getSession()`/`getUserId()` (synchronous, cache-only,
 * can never hang) instead of calling `supabase.auth.*` directly.
 *
 * Part 1 of the Auth/Loading fix migrates exactly 4 call sites to this
 * module: App.tsx, contexts/UserContext.tsx, components/LoginPage.tsx and
 * lib/authenticated-fetch.ts. The ~90 other direct `supabase.auth.*` call
 * sites across features/** are Part 2 (a separate, later migration) and are
 * deliberately left untouched here.
 */

// ============================================================================
// Centralized timeouts -- the single place these durations are defined.
// Timeout is never treated as "invalid session" here, only as "we couldn't
// confirm in time" -- callers decide how to fail open/closed per context.
// ============================================================================

/** verifySessionIsLive() at app boot -- confirms the cached session's
 *  account still exists server-side. */
export const AUTH_BOOT_TIMEOUT_MS = 8000;

/** Re-sync check fired once when the tab becomes visible again after being
 *  backgrounded/suspended. */
export const AUTH_VISIBILITY_RECHECK_TIMEOUT_MS = 5000;

/** UserContext's user_company_relation query. Not consumed directly by
 *  this module -- exported here so it lives next to the other two instead
 *  of being a magic number in UserContext.tsx. */
export const COMPANY_FETCH_TIMEOUT_MS = 5000;

/** signOutLocally() -- how long to wait for Supabase's /logout before
 *  clearing the local session anyway. */
export const SIGN_OUT_TIMEOUT_MS = 3000;

// ============================================================================
// In-memory cache + subscriber list
// ============================================================================

let currentSession: Session | null = null;

type Listener = () => void;
const listeners = new Set<Listener>();

function notifyListeners(): void {
  listeners.forEach((listener) => listener());
}

function setSession(session: Session | null): void {
  currentSession = session;
  notifyListeners();
}

/** Synchronous, cache-only read -- never calls Supabase, can never hang.
 *  This is what the rest of the app should call instead of
 *  `await supabase.auth.getSession()`. */
export function getSession(): Session | null {
  return currentSession;
}

export function getUserId(): string | null {
  return currentSession?.user?.id ?? null;
}

/** Subscribe to cache changes (any onAuthStateChange event or the initial
 *  seed resolving). Matches the `subscribe` shape React's
 *  `useSyncExternalStore` expects -- see useSession() below. */
export function onSessionChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// ============================================================================
// One-time initial seed -- the only "cold" supabase.auth.getSession() call
// in the whole app. Wrapped in a timeout: if it hangs, the app starts with
// no cached session rather than never starting at all. Callers that need
// to know once this has settled should await waitForInitialSession().
// ============================================================================

let seedPromise: Promise<Session | null> | null = null;

function seedInitialSession(): Promise<Session | null> {
  if (seedPromise) return seedPromise;

  seedPromise = (async () => {
    try {
      const {
        data: { session },
      } = await withTimeout(supabase.auth.getSession(), AUTH_BOOT_TIMEOUT_MS, 'Initial getSession timed out');
      setSession(session);
      return session;
    } catch (error) {
      console.warn('[auth-session] Initial getSession timed out or failed -- starting with no cached session:', error);
      setSession(null);
      return null;
    }
  })();

  return seedPromise;
}

/** Resolves once the initial session has been seeded (or the boot timeout
 *  gave up). Safe to call from multiple places concurrently -- they all
 *  share the same underlying call, so this never triggers a second real
 *  getSession(). */
export function waitForInitialSession(): Promise<Session | null> {
  return seedInitialSession();
}

// ============================================================================
// signOutLocally -- ends THIS browser's session without depending on the
// network. supabase.auth.signOut() needs a round trip to Supabase and, when
// that fails (retryable error), returns the error WITHOUT removing the local
// session -- so a Logout click did nothing visible, and /login then found
// the still-valid session and bounced back to '/'. Here the /logout call is
// bounded and best-effort (scope 'local': revokes only this session, never
// the user's other devices), and the local session is cleared regardless.
// ============================================================================

export async function signOutLocally(): Promise<void> {
  try {
    await withTimeout(supabase.auth.signOut({ scope: 'local' }), SIGN_OUT_TIMEOUT_MS, 'signOut timed out');
  } catch {
    // Best-effort -- the local cleanup below is what matters.
  }
  Object.keys(localStorage)
    .filter((key) => /^sb-.+-auth-token$/.test(key))
    .forEach((key) => localStorage.removeItem(key));
  setSession(null);
}

// ============================================================================
// verifySessionIsLive -- the ONLY function in the app allowed to make a
// real getUser() round-trip (confirms the account hasn't been deleted/
// deactivated server-side; getSession() alone only reads the local cache).
// Fails open on timeout: a slow network is not evidence of an invalid
// session, so a timeout must never be treated the same as a confirmed-bad
// account.
// ============================================================================

export type SessionLiveness = 'valid' | 'invalid' | 'timeout';

export async function verifySessionIsLive(timeoutMs: number = AUTH_BOOT_TIMEOUT_MS): Promise<SessionLiveness> {
  try {
    const {
      data: { user },
      error,
    } = await withTimeout(supabase.auth.getUser(), timeoutMs, 'getUser timed out');
    if (error || !user) return 'invalid';
    return 'valid';
  } catch (error) {
    return 'timeout';
  }
}

// ============================================================================
// The single onAuthStateChange subscription for the entire app.
//
// IMPORTANT: this callback must never await another supabase.auth.* or
// supabase.from() call. GoTrueClient awaits this callback from inside the
// same lock it's still holding while emitting the event -- an awaited
// Supabase call in here creates a circular wait on that lock and deadlocks
// the client forever (reproduced live in this codebase, see the identical
// warning this replaces in the old UserContext.tsx). This callback only
// ever does a synchronous cache assignment + listener notification.
// ============================================================================

function subscribeToAuthChanges(): void {
  supabase.auth.onAuthStateChange((event, session) => {
    switch (event) {
      case 'SIGNED_IN':
      case 'TOKEN_REFRESHED':
      case 'USER_UPDATED':
        // All three carry the current session (a new one, or the same one
        // with a refreshed access_token) -- just mirror it.
        setSession(session);
        break;
      case 'SIGNED_OUT':
        setSession(null);
        break;
      default:
        // INITIAL_SESSION, PASSWORD_RECOVERY, MFA_CHALLENGE_VERIFIED, etc.
        // -- still just mirror whatever Supabase says the session is.
        setSession(session);
        break;
    }
  });
}

// ============================================================================
// visibilitychange -- ONE global listener for the whole app (not one per
// page/component). When the tab regains focus after being backgrounded/
// suspended (where supabase-js's own background refresh timer is throttled
// by the browser), force a single real getSession() to resync -- supabase-js
// refreshes internally if the cached token is stale. Bounded by a short
// timeout; on timeout, keep whatever is currently cached rather than
// clearing it, and never sign out just because this check didn't finish in
// time.
// ============================================================================

let visibilityCheckInFlight = false;

async function handleVisibilityChange(): Promise<void> {
  if (document.visibilityState !== 'visible') return;
  if (visibilityCheckInFlight) return;

  visibilityCheckInFlight = true;
  try {
    const {
      data: { session },
      error,
    } = await withTimeout(supabase.auth.getSession(), AUTH_VISIBILITY_RECHECK_TIMEOUT_MS, 'Visibility getSession timed out');
    if (!error) {
      setSession(session);
    }
    // On an error (not a timeout) that isn't a hard failure, leave the
    // cache untouched rather than guessing.
  } catch (error) {
    // Timed out -- keep the cached session, try again on the next
    // visibility change instead of guessing now.
    console.warn('[auth-session] Visibility re-sync timed out -- keeping cached session:', error);
  } finally {
    visibilityCheckInFlight = false;
  }
}

// ============================================================================
// Module initialization -- runs exactly once, the moment this module is
// first imported (which authenticated-fetch.ts already documents must
// happen as early as possible, before any component calls fetch()).
// ============================================================================

subscribeToAuthChanges();
seedInitialSession();
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', handleVisibilityChange);
}
